import { authConfig } from '../config';

/**
 * Reads the allowlist of `redirectUri` values accepted by the Sync Hosted SSO flow.
 *
 * Client-safe: reads from `NEXT_PUBLIC_SYNC_ALLOWED_REDIRECT_URIS` (CSV). The
 * canonical app default (`authConfig.redirectUri`) is appended automatically
 * so dev environments work without extra configuration.
 *
 * Multi-runtime: because it relies on `process.env`, in React Native this list
 * must be supplied via the bootstrap. Once the module is extracted as a
 * library, the source will move to `SyncConfig.allowedRedirectUris` while
 * keeping the same signature.
 */
export function getAllowedRedirectUris(): string[] {
  const raw =
    (typeof process !== 'undefined' &&
      process.env?.NEXT_PUBLIC_SYNC_ALLOWED_REDIRECT_URIS) ||
    '';
  const fromEnv = raw
    .split(',')
    .map((uri) => uri.trim())
    .filter(Boolean);

  const defaultCallback = authConfig.redirectUri;
  if (defaultCallback && !fromEnv.includes(defaultCallback)) {
    fromEnv.push(defaultCallback);
  }

  return fromEnv;
}

/**
 * Validates `redirectUri` against the allowlist using **exact match**.
 *
 * Guard rail (do not relax): no wildcards, no `startsWith`, no regex, no
 * path/host normalisation. The comparison must be byte-for-byte to prevent
 * bypass via suffixes, encoding tricks, or malicious subdomains.
 */
export function isAllowedSyncRedirectUri(
  candidate: string,
  allowlist: string[],
): boolean {
  if (!candidate) return false;
  return allowlist.includes(candidate);
}
